import { useState, useEffect, useRef, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import Editor, { DiffEditor } from '@monaco-editor/react';
import { INCIDENTS_API } from '@/lib/incidentsApi';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import {
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle,
} from '@/components/ui/dialog';
import { toast } from 'sonner';
import {
  ArrowLeft, Loader2, CheckCircle2, XCircle, AlertTriangle, GitCompare, Upload, TestTube,
  ShieldCheck, Boxes, Wand2, Copy, History, Lock, PencilLine,
} from 'lucide-react';
import { pickActiveCodeGeneration } from '@/lib/ticketCodeHelpers';
import {
  deployAbapCode, runAbapAtc, startAbapAtcRemediation, waitForAbapAtcRemediation, formatApiError,
} from '@/lib/abapDeployApi';
import { runAbapSyntaxCheck, getLiveAbapSource, fixAbapCode, parseImportTicketTitle } from '@/lib/abapImportApi';
import { usePermissions } from '@/contexts/PermissionsContext';
import { permissionDeniedTitle } from '@/lib/permissionUi';
import { useAuth } from '@/contexts/AuthContext';

const API = INCIDENTS_API;

function extractCodeFromBackticks(content) {
  if (!content) return content;
  const matches = content.match(/```[\s\S]*?```/g);
  if (!matches || matches.length === 0) return content;
  return matches
    .map((m) => m.replace(/^```[\w]*\n?/, '').replace(/\n?```$/, ''))
    .join('\n\n');
}

const STAGE_META = {
  imported: { label: 'Imported', className: 'bg-slate-500 text-white', card: 'ticket-card--default' },
  validated: { label: 'Syntax OK', className: 'bg-cp-mod text-cp-fg', card: 'ticket-card--test' },
  validation_failed: { label: 'Syntax Failed', className: 'bg-cp-crit text-white', card: 'ticket-card--failed' },
  deploying: { label: 'Deploying…', className: 'bg-cp-imp text-white', card: 'ticket-card--deploy' },
  atc_remediating: { label: 'Running ATC…', className: 'bg-cp-imp text-white', card: 'ticket-card--deploy' },
  atc_clean: { label: 'ATC Clean', className: 'bg-cp-ok text-white', card: 'ticket-card--done' },
  atc_partial: { label: 'ATC Partial', className: 'bg-cp-imp text-white', card: 'ticket-card--deploy' },
  validated_and_deployed: { label: 'Deployed', className: 'bg-emerald-600 text-white', card: 'ticket-card--done' },
};

const DEPLOYED_STAGE = 'validated_and_deployed';

function stageMeta(stage) {
  return STAGE_META[stage] || STAGE_META.imported;
}

/** Best-effort human label for an audit action key, e.g. "syntax_check" -> "Syntax check". */
function formatAuditAction(action) {
  const s = String(action || '').replace(/_/g, ' ').trim();
  return s ? s.charAt(0).toUpperCase() + s.slice(1) : 'Activity';
}

const AUDIT_ICONS = {
  ticket_created: Boxes,
  syntax_check: ShieldCheck,
  atc_check: TestTube,
  code_fixed_ai: Wand2,
  deploy: Upload,
  deploy_and_atc: TestTube,
};

export default function AbapTransportDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { hasPermission } = usePermissions();
  const { user } = useAuth();
  const canDeploy = hasPermission('tickets', 'update');
  const actor = user?.email || user?.name || 'user';

  const [ticket, setTicket] = useState(null);
  const [currentCode, setCurrentCode] = useState('');
  const [loading, setLoading] = useState(true);

  const [syntaxChecking, setSyntaxChecking] = useState(false);
  const [syntaxResult, setSyntaxResult] = useState(null);

  const [atcChecking, setAtcChecking] = useState(false);
  const [atcResult, setAtcResult] = useState(null);

  const [fixingSource, setFixingSource] = useState(''); // '' | 'syntax' | 'atc'

  const [deploying, setDeploying] = useState(false);
  const [deployMessage, setDeployMessage] = useState('');

  const [diffOpen, setDiffOpen] = useState(false);
  const [diffLoading, setDiffLoading] = useState(false);
  const [liveSource, setLiveSource] = useState('');
  const [diffError, setDiffError] = useState('');
  const [diffMode, setDiffMode] = useState('deploy'); // 'deploy' | 'deploy-and-atc'

  const [auditLogs, setAuditLogs] = useState([]);
  const [auditLoading, setAuditLoading] = useState(true);

  // Snapshot of the code as of the last successful deploy — used to block
  // re-deploying unchanged code and to warn on navigating away with edits.
  const [lastDeployedCode, setLastDeployedCode] = useState(null);

  const editorRef = useRef(null);
  const monacoRef = useRef(null);

  const { objectName, objectType } = ticket
    ? parseImportTicketTitle(ticket.title)
    : { objectName: '', objectType: 'PROG' };

  const fetchTicketAndCode = useCallback(async () => {
    try {
      const [ticketRes, codeRes] = await Promise.all([
        axios.get(`${API}/tickets/${id}`),
        axios.get(`${API}/code/${id}`),
      ]);
      setTicket(ticketRes.data);
      const generations = Array.isArray(codeRes.data) ? codeRes.data : [];
      const active = pickActiveCodeGeneration(generations);
      const raw = active?.code || '';
      const code = raw.includes('```') ? extractCodeFromBackticks(raw) : raw;
      setCurrentCode(code);
      if (ticketRes.data?.abap_workflow_stage === DEPLOYED_STAGE) {
        setLastDeployedCode((prev) => (prev === null ? code : prev));
      }
    } catch (error) {
      console.error('Error loading ABAP transport:', error);
      toast.error(formatApiError(error));
    } finally {
      setLoading(false);
    }
  }, [id]);

  const fetchAuditLogs = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API}/tickets/${id}/audit-logs`);
      setAuditLogs(Array.isArray(data) ? data : []);
    } catch (error) {
      // Non-fatal — audit trail is a supplementary view.
      console.error('Error loading audit logs:', error);
    } finally {
      setAuditLoading(false);
    }
  }, [id]);

  useEffect(() => { fetchTicketAndCode(); }, [fetchTicketAndCode]);
  useEffect(() => { fetchAuditLogs(); }, [fetchAuditLogs]);

  /** Best-effort audit trail entry — never blocks the calling action on failure. */
  const logAudit = useCallback(async (action, details = {}) => {
    try {
      const { data } = await axios.post(`${API}/tickets/${id}/audit-logs`, { action, actor, details });
      setAuditLogs((prev) => [...prev, data].filter(Boolean));
    } catch (error) {
      console.error('Error appending audit log:', error);
    }
  }, [id, actor]);

  const persistTicketFields = async (fields) => {
    await axios.put(`${API}/tickets/${id}`, fields);
  };

  const hasCode = !!(currentCode && currentCode.trim());
  const isDeployedStage = ticket?.abap_workflow_stage === DEPLOYED_STAGE;
  const isDirty = lastDeployedCode === null ? false : currentCode !== lastDeployedCode;
  // Once deployed, re-deploying identical code is a no-op — block until something changes.
  const deployBlocked = isDeployedStage && !isDirty;

  useEffect(() => {
    const handler = (e) => {
      if (!isDirty) return;
      e.preventDefault();
      e.returnValue = '';
    };
    window.addEventListener('beforeunload', handler);
    return () => window.removeEventListener('beforeunload', handler);
  }, [isDirty]);

  const setSyntaxMarkers = useCallback((errors) => {
    const editor = editorRef.current;
    const monaco = monacoRef.current;
    if (!editor || !monaco) return;
    const model = editor.getModel();
    if (!model) return;
    const markers = (errors || [])
      .filter((e) => e && typeof e === 'object')
      .map((e) => {
        const line = Math.max(1, Number(e.line) || 1);
        const severity = String(e.severity || 'E').toUpperCase();
        return {
          startLineNumber: line,
          endLineNumber: line,
          startColumn: 1,
          endColumn: 1000,
          message: e.text || e.message || 'Syntax issue',
          severity: severity === 'E' ? monaco.MarkerSeverity.Error
            : severity === 'W' ? monaco.MarkerSeverity.Warning
            : monaco.MarkerSeverity.Info,
        };
      });
    monaco.editor.setModelMarkers(model, 'syntax-check', markers);
  }, []);

  /** Manual edits (typing, or a Fix-with-AI replacing the code) invalidate prior check results. */
  const handleCodeChange = (value) => {
    setCurrentCode(value || '');
    setSyntaxResult(null);
    setAtcResult(null);
    setSyntaxMarkers([]);
  };

  const handleEditorMount = (editor, monaco) => {
    editorRef.current = editor;
    monacoRef.current = monaco;
  };

  const copyToClipboard = async (value, label) => {
    try {
      await navigator.clipboard.writeText(value);
      toast.success(`${label} copied`);
    } catch {
      toast.error(`Could not copy ${label.toLowerCase()}`);
    }
  };

  const handleSyntaxCheck = async () => {
    if (!hasCode) return;
    setSyntaxChecking(true);
    setSyntaxResult(null);
    try {
      const result = await runAbapSyntaxCheck({ code: currentCode, objectName: objectName || 'UNKNOWN' });
      setSyntaxResult(result);
      setSyntaxMarkers(result.status === 'fail' ? result.errors : []);
      const nextStage = result.status === 'fail' ? 'validation_failed' : 'validated';
      await persistTicketFields({ abap_workflow_stage: nextStage });
      setTicket((prev) => ({ ...(prev || {}), abap_workflow_stage: nextStage }));
      await logAudit('syntax_check', { status: result.status, object_name: result.object_name, error_count: (result.errors || []).length });
      if (result.status === 'fail') {
        toast.error('Syntax check found errors — see details below');
      } else {
        toast.success('Syntax check passed');
      }
    } catch (error) {
      toast.error(formatApiError(error));
    } finally {
      setSyntaxChecking(false);
    }
  };

  /** Standalone ATC — runs against the object's current live SAP source (no deploy required, since Import tickets already exist in SAP). */
  const handleRunAtc = async () => {
    if (!objectName) return;
    setAtcChecking(true);
    setAtcResult(null);
    try {
      const result = await runAbapAtc({ objectName, objectType, ticketNumber: id });
      setAtcResult(result);
      await logAudit('atc_check', { status: result.status, p1: result.p1 || 0, p2: result.p2 || 0, p3: result.p3 || 0 });
      if ((result.p1 || 0) > 0 || (result.p2 || 0) > 0) {
        toast.error(`ATC found P1=${result.p1 || 0}, P2=${result.p2 || 0} — see details below`);
      } else {
        toast.success(result.message || 'ATC clean');
      }
    } catch (error) {
      toast.error(formatApiError(error));
    } finally {
      setAtcChecking(false);
    }
  };

  /** AI-fix from the last syntax/ATC report — updates the local editor only, never deploys. */
  const handleFix = async (source) => {
    if (!hasCode) return;
    setFixingSource(source);
    try {
      const result = await fixAbapCode({
        code: currentCode,
        source,
        syntaxReport: source === 'syntax' ? syntaxResult?.report : null,
        atcResult: source === 'atc' ? atcResult : null,
        originalRequest: ticket?.description || ticket?.title || '',
        ticketNumber: id,
      });
      setCurrentCode(result.code || currentCode);
      setSyntaxResult(null);
      setAtcResult(null);
      setSyntaxMarkers([]);
      await logAudit('code_fixed_ai', { source });
      toast.success('Code corrected locally — re-run checks or review the diff before deploying.');
    } catch (error) {
      toast.error(formatApiError(error));
    } finally {
      setFixingSource('');
    }
  };

  const openDiff = async (mode) => {
    if (!hasCode || !objectName || deployBlocked) return;
    setDiffMode(mode);
    setDiffOpen(true);
    setDiffLoading(true);
    setDiffError('');
    try {
      const result = await getLiveAbapSource({ objectName, objectType });
      setLiveSource(result.source || '');
    } catch (error) {
      setDiffError(formatApiError(error));
      setLiveSource('');
    } finally {
      setDiffLoading(false);
    }
  };

  const runDeploy = async () => {
    setDiffOpen(false);
    setDeploying(true);
    setDeployMessage('');
    const codeAtDeploy = currentCode;
    try {
      await persistTicketFields({ abap_workflow_stage: 'deploying' });
      setTicket((prev) => ({ ...(prev || {}), abap_workflow_stage: 'deploying' }));

      const result = await deployAbapCode({
        code: codeAtDeploy,
        objectName,
        objectType,
        description: ticket?.description || '',
        ticketNumber: id,
        ticketTypeKey: 'Import',
      });

      const nextStage = result.success ? DEPLOYED_STAGE : 'validated';
      await persistTicketFields({
        abap_workflow_stage: nextStage,
        deployed_object_name: result.object_name || objectName,
      });
      setTicket((prev) => ({ ...(prev || {}), abap_workflow_stage: nextStage, deployed_object_name: result.object_name || objectName }));
      setDeployMessage(result.message || (result.success ? 'Deploy completed.' : 'Deploy failed.'));
      await logAudit('deploy', { success: result.success, object_name: result.object_name || objectName, message: result.message || '' });
      if (result.success) {
        setLastDeployedCode(codeAtDeploy);
        toast.success(result.message || 'Deployed successfully');
      } else {
        toast.error(result.message || 'Deploy failed');
      }
    } catch (error) {
      const errText = formatApiError(error);
      setDeployMessage(errText);
      toast.error(errText);
      await logAudit('deploy', { success: false, message: errText });
      try {
        await persistTicketFields({ abap_workflow_stage: 'validated' });
      } catch {}
    } finally {
      setDeploying(false);
    }
  };

  const runDeployAndAtc = async () => {
    setDiffOpen(false);
    setDeploying(true);
    setDeployMessage('ATC remediation started…');
    const codeAtDeploy = currentCode;
    try {
      await persistTicketFields({ abap_workflow_stage: 'atc_remediating', deployed_object_name: objectName });
      setTicket((prev) => ({ ...(prev || {}), abap_workflow_stage: 'atc_remediating', deployed_object_name: objectName }));

      const start = await startAbapAtcRemediation({
        code: codeAtDeploy,
        objectName,
        objectType,
        description: ticket?.description || '',
        ticketNumber: id,
        originalRequest: ticket?.description || ticket?.title || '',
      });
      if (start?.status === 'error') {
        throw new Error(start.message || 'Failed to start ATC remediation');
      }

      const outcome = await waitForAbapAtcRemediation({
        ticketNumber: id,
        fetchTicket: async () => {
          const { data } = await axios.get(`${API}/tickets/${id}`);
          setTicket((prev) => ({ ...(prev || {}), ...data }));
          return data;
        },
        fetchCode: async () => {
          try {
            const codeRes = await axios.get(`${API}/code/${id}`);
            const active = pickActiveCodeGeneration(Array.isArray(codeRes.data) ? codeRes.data : []);
            return active?.code || '';
          } catch {
            return '';
          }
        },
      });

      const finalCode = (outcome.code || '').trim() || codeAtDeploy;
      setCurrentCode(finalCode);
      const message = outcome.success
        ? 'ATC remediation complete — P1=P2=0 (P3 ignored), syntax clean.'
        : 'ATC remediation incomplete — kept best syntax-clean version.';
      setDeployMessage(message);
      await logAudit('deploy_and_atc', { success: outcome.success, stage: outcome.stage });

      if (outcome.success) {
        // Normalize to the same terminal "Deployed" status plain Deploy uses,
        // so both actions gate identically until code changes again.
        await persistTicketFields({ abap_workflow_stage: DEPLOYED_STAGE, deployed_object_name: outcome.objectName || objectName });
        setTicket((prev) => ({ ...(prev || {}), abap_workflow_stage: DEPLOYED_STAGE, deployed_object_name: outcome.objectName || objectName }));
        setLastDeployedCode(finalCode);
        toast.success(message);
      } else {
        toast.warning(message);
      }
      await fetchTicketAndCode();
    } catch (error) {
      const errText = formatApiError(error);
      setDeployMessage(errText);
      toast.error(errText);
      await logAudit('deploy_and_atc', { success: false, message: errText });
      try {
        await persistTicketFields({ abap_workflow_stage: 'validated', deployed_object_name: objectName });
      } catch {}
    } finally {
      setDeploying(false);
    }
  };

  if (loading) {
    return (
      <div className="flex flex-col items-center justify-center h-full gap-3 animate-in fade-in duration-300">
        <Loader2 className="h-8 w-8 animate-spin text-cp-violet" />
        <p className="text-sm text-muted-foreground">Loading transport…</p>
      </div>
    );
  }

  if (!ticket) {
    return <div className="flex items-center justify-center h-full">Transport not found</div>;
  }

  const meta = stageMeta(ticket.abap_workflow_stage);

  return (
    <div className="space-y-6 animate-in fade-in slide-in-from-bottom-2 duration-300" data-testid="abap-transport-detail">
      <div className="flex items-center gap-3">
        <Button variant="ghost" size="icon" onClick={() => navigate('/abap-transports')} data-testid="back-to-transports">
          <ArrowLeft className="h-5 w-5" />
        </Button>
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            <button
              type="button"
              onClick={() => copyToClipboard(ticket.ticket_number, 'Ticket number')}
              className="group inline-flex items-center gap-1 rounded-md border border-cp-faint/60 px-2 py-0.5 font-mono text-xs text-muted-foreground hover:border-cp-violet hover:text-cp-violet transition-colors"
              title="Copy ticket number"
            >
              {ticket.ticket_number}
              <Copy className="h-3 w-3 opacity-0 group-hover:opacity-100 transition-opacity" />
            </button>
            <Badge className={`text-xs transition-colors ${meta.className}`}>{meta.label}</Badge>
            <Badge variant="outline" className="text-xs">{objectType}</Badge>
            {isDirty && (
              <Badge variant="outline" className="text-xs gap-1 border-cp-mod/60 text-cp-mod" data-testid="unsaved-changes-badge">
                <PencilLine className="h-3 w-3" />
                Unsaved changes
              </Badge>
            )}
          </div>
          <h1 className="text-2xl font-bold tracking-tight mt-1 truncate">{objectName || ticket.title}</h1>
          <p className="text-muted-foreground text-sm mt-0.5">{ticket.sap_system} &middot; imported {new Date(ticket.created_at).toLocaleString()}</p>
        </div>
      </div>

      <Card className={`ticket-card ${meta.card} transition-[transform,box-shadow,border-color] duration-300`}>
        <CardHeader className="flex flex-row items-center justify-between space-y-0 flex-wrap gap-2">
          <CardTitle className="text-base flex items-center gap-2">
            <Boxes className="h-4 w-4" />
            Source
          </CardTitle>
          <div className="flex items-center gap-2 flex-wrap justify-end">
            <span title={permissionDeniedTitle(canDeploy, 'run syntax check')}>
              <Button size="sm" variant="outline" onClick={handleSyntaxCheck} disabled={!canDeploy || !hasCode || syntaxChecking} data-testid="syntax-check-button">
                {syntaxChecking ? (<><Loader2 className="h-4 w-4 animate-spin mr-1" />Checking…</>) : (<><ShieldCheck className="h-4 w-4 mr-1" />Syntax Check</>)}
              </Button>
            </span>
            <span title={permissionDeniedTitle(canDeploy, 'run ATC on the live SAP object')}>
              <Button size="sm" variant="outline" onClick={handleRunAtc} disabled={!canDeploy || !objectName || atcChecking} data-testid="run-atc-button">
                {atcChecking ? (<><Loader2 className="h-4 w-4 animate-spin mr-1" />Running ATC…</>) : (<><TestTube className="h-4 w-4 mr-1" />Run ATC</>)}
              </Button>
            </span>
            <span title={deployBlocked ? 'Already deployed — edit the code to deploy again' : permissionDeniedTitle(canDeploy, 'view diff and deploy')}>
              <Button size="sm" variant="outline" onClick={() => openDiff('deploy')} disabled={!canDeploy || !hasCode || deploying || deployBlocked} data-testid="view-diff-deploy-button">
                {deployBlocked ? (<><Lock className="h-4 w-4 mr-1" />Deployed</>) : (<><GitCompare className="h-4 w-4 mr-1" />Deploy…</>)}
              </Button>
            </span>
            <span title={deployBlocked ? 'Already deployed — edit the code to deploy again' : permissionDeniedTitle(canDeploy, 'view diff, deploy and run ATC')}>
              <Button size="sm" variant="outline" onClick={() => openDiff('deploy-and-atc')} disabled={!canDeploy || !hasCode || deploying || deployBlocked} data-testid="view-diff-deploy-atc-button">
                {deploying ? (<><Loader2 className="h-4 w-4 animate-spin mr-1" />Working…</>)
                  : deployBlocked ? (<><Lock className="h-4 w-4 mr-1" />Deployed</>)
                  : (<><TestTube className="h-4 w-4 mr-1" />Deploy &amp; Run ATC…</>)}
              </Button>
            </span>
          </div>
        </CardHeader>
        <CardContent className="space-y-3">
          {syntaxResult && (
            <div className={`rounded-md border p-3 text-sm animate-in fade-in duration-200 ${syntaxResult.status === 'fail' ? 'border-cp-crit/40 bg-cp-crit/5' : 'border-cp-ok/40 bg-cp-ok/5'}`}>
              <div className="flex items-start justify-between gap-3">
                <div className="flex items-start gap-2 min-w-0">
                  {syntaxResult.status === 'fail' ? <XCircle className="h-4 w-4 text-cp-crit mt-0.5 shrink-0" /> : <CheckCircle2 className="h-4 w-4 text-cp-ok mt-0.5 shrink-0" />}
                  <pre className="whitespace-pre-wrap font-sans">{syntaxResult.message}</pre>
                </div>
                {syntaxResult.status === 'fail' && (
                  <span title={permissionDeniedTitle(canDeploy, 'fix code with AI')} className="shrink-0">
                    <Button size="sm" variant="outline" onClick={() => handleFix('syntax')} disabled={!canDeploy || fixingSource !== ''} data-testid="fix-syntax-button">
                      {fixingSource === 'syntax' ? (<><Loader2 className="h-4 w-4 animate-spin mr-1" />Fixing…</>) : (<><Wand2 className="h-4 w-4 mr-1" />Fix with AI</>)}
                    </Button>
                  </span>
                )}
              </div>
            </div>
          )}
          {atcResult && (
            <div className={`rounded-md border p-3 text-sm animate-in fade-in duration-200 ${(atcResult.p1 > 0 || atcResult.p2 > 0) ? 'border-cp-crit/40 bg-cp-crit/5' : 'border-cp-ok/40 bg-cp-ok/5'}`}>
              <div className="flex items-start justify-between gap-3">
                <div className="flex items-start gap-2 min-w-0">
                  {(atcResult.p1 > 0 || atcResult.p2 > 0) ? <AlertTriangle className="h-4 w-4 text-cp-crit mt-0.5 shrink-0" /> : <CheckCircle2 className="h-4 w-4 text-cp-ok mt-0.5 shrink-0" />}
                  <div>
                    <div>{atcResult.message || `ATC — P1=${atcResult.p1 || 0}, P2=${atcResult.p2 || 0}, P3=${atcResult.p3 || 0}`}</div>
                    {atcResult.findings?.length > 0 && (
                      <ul className="mt-1 space-y-0.5 text-xs text-muted-foreground list-disc list-inside">
                        {atcResult.findings.slice(0, 8).map((f, i) => (
                          <li key={i}>[P{f.priority ?? f.severity ?? '?'}] {f.message_title || f.message || f.text || 'Finding'}</li>
                        ))}
                      </ul>
                    )}
                  </div>
                </div>
                {(atcResult.p1 > 0 || atcResult.p2 > 0) && (
                  <span title={permissionDeniedTitle(canDeploy, 'fix code with AI')} className="shrink-0">
                    <Button size="sm" variant="outline" onClick={() => handleFix('atc')} disabled={!canDeploy || fixingSource !== ''} data-testid="fix-atc-button">
                      {fixingSource === 'atc' ? (<><Loader2 className="h-4 w-4 animate-spin mr-1" />Fixing…</>) : (<><Wand2 className="h-4 w-4 mr-1" />Fix with AI</>)}
                    </Button>
                  </span>
                )}
              </div>
            </div>
          )}
          {deployMessage && (
            <div className="rounded-md border p-3 text-sm bg-muted/40 animate-in fade-in duration-200">
              <pre className="whitespace-pre-wrap font-sans">{deployMessage}</pre>
            </div>
          )}
          <div className="rounded-md border overflow-hidden">
            <Editor
              height="60vh"
              defaultLanguage="plaintext"
              value={currentCode}
              onChange={handleCodeChange}
              onMount={handleEditorMount}
              theme="vs-dark"
              options={{ minimap: { enabled: false }, fontSize: 13, wordWrap: 'on', readOnly: deploying }}
            />
          </div>
        </CardContent>
      </Card>

      <Card className="animate-in fade-in slide-in-from-bottom-2 duration-300">
        <CardHeader>
          <CardTitle className="text-base flex items-center gap-2">
            <History className="h-4 w-4" />
            Activity
          </CardTitle>
          <CardDescription>Complete history of actions on this transport</CardDescription>
        </CardHeader>
        <CardContent>
          {auditLoading ? (
            <div className="flex items-center justify-center py-8">
              <Loader2 className="h-5 w-5 animate-spin text-muted-foreground" />
            </div>
          ) : auditLogs.length === 0 ? (
            <div className="text-center py-8 text-sm text-muted-foreground">No activity yet</div>
          ) : (
            <div className="space-y-2 max-h-96 overflow-y-auto" data-testid="audit-log-list">
              {[...auditLogs].reverse().map((log) => {
                const Icon = AUDIT_ICONS[log.action] || History;
                return (
                  <div key={log.id} className="flex items-start gap-3 p-3 border rounded-lg text-sm hover:bg-accent transition-colors" data-testid={`audit-log-${log.id}`}>
                    <Icon className="h-4 w-4 mt-0.5 text-cp-violet shrink-0" />
                    <div className="flex-1 min-w-0">
                      <div className="font-medium">{formatAuditAction(log.action)}</div>
                      {log.details && Object.keys(log.details).length > 0 && (
                        <div className="text-xs text-muted-foreground mt-0.5 truncate">
                          {Object.entries(log.details).map(([k, v]) => `${k}: ${v}`).join(' · ')}
                        </div>
                      )}
                      <div className="text-xs text-muted-foreground mt-1">
                        {log.timestamp ? new Date(log.timestamp).toLocaleString() : ''} &middot; by {log.actor}
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </CardContent>
      </Card>

      <Dialog open={diffOpen} onOpenChange={setDiffOpen}>
        <DialogContent className="max-w-5xl">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <GitCompare className="h-5 w-5" />
              Review changes before deploy
            </DialogTitle>
            <DialogDescription>
              Left: current live source in SAP for <span className="font-mono">{objectName}</span>. Right: what will be written.
            </DialogDescription>
          </DialogHeader>

          {diffLoading ? (
            <div className="flex items-center justify-center py-16">
              <Loader2 className="h-6 w-6 animate-spin" />
            </div>
          ) : diffError ? (
            <div className="rounded-md border border-cp-crit/40 bg-cp-crit/5 p-4 text-sm text-cp-crit">
              Could not load live source for comparison: {diffError}
            </div>
          ) : (
            <div className="rounded-md border overflow-hidden">
              <DiffEditor
                height="55vh"
                original={liveSource}
                modified={currentCode}
                originalLanguage="plaintext"
                modifiedLanguage="plaintext"
                theme="vs-dark"
                options={{ readOnly: true, renderSideBySide: true, minimap: { enabled: false }, fontSize: 13 }}
              />
            </div>
          )}

          <DialogFooter>
            <Button variant="outline" onClick={() => setDiffOpen(false)}>Cancel</Button>
            <span title={permissionDeniedTitle(canDeploy, 'deploy ticket code')}>
              <Button
                onClick={diffMode === 'deploy-and-atc' ? runDeployAndAtc : runDeploy}
                disabled={!canDeploy || diffLoading || !!diffError}
                data-testid="confirm-deploy-button"
              >
                {diffMode === 'deploy-and-atc' ? (<><TestTube className="h-4 w-4 mr-1" />Deploy &amp; Run ATC</>) : (<><Upload className="h-4 w-4 mr-1" />Deploy</>)}
              </Button>
            </span>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
